if (!customElements.get('reviews-slider')) {
  class ReviewsSlider extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('.reviews-grid');
      this.nav = this.querySelector('.reviews-nav');
      this.prevButton = this.querySelector('.reviews-nav__btn--prev');
      this.nextButton = this.querySelector('.reviews-nav__btn--next');
      this.dotsContainer = this.querySelector('.reviews-nav__dots');
      if (!this.grid || !this.nav || !this.prevButton || !this.nextButton || !this.dotsContainer) return;

      this.pages = 0;
      this.page = 0;
      this.pageWidth = 0;
      this.frame = null;

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };

      this.grid.addEventListener('scroll', this.onScroll, { passive: true });
      this.prevButton.addEventListener('click', () => this.scrollByPage(-1));
      this.nextButton.addEventListener('click', () => this.scrollByPage(1));
      this.dotsContainer.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-page]');
        if (dot) this.goTo(Number(dot.dataset.page));
      });

      this.resizeObserver = new ResizeObserver(() => this.build());
      this.resizeObserver.observe(this.grid);
      this.build();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.grid?.removeEventListener('scroll', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    build() {
      const { clientWidth, scrollWidth } = this.grid;
      const gap = parseFloat(getComputedStyle(this.grid).columnGap) || 0;

      this.pageWidth = clientWidth + gap;
      const pages = scrollWidth - clientWidth < 2 ? 1 : Math.ceil((scrollWidth + gap - 1) / this.pageWidth);

      if (pages !== this.pages) {
        this.pages = pages;
        this.renderDots();
      }

      this.nav.hidden = pages <= 1;
      this.update();
    }

    renderDots() {
      const template = this.dotsContainer.dataset.dotLabel || '';
      this.dotsContainer.replaceChildren(
        ...Array.from({ length: this.pages }, (_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'reviews-nav__dot';
          dot.dataset.page = String(index);
          dot.setAttribute(
            'aria-label',
            template.replace('__SLIDE__', index + 1).replace('__TOTAL__', this.pages)
          );
          return dot;
        })
      );
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.grid;
      const maxScroll = scrollWidth - clientWidth;
      const atEnd = scrollLeft >= maxScroll - 2;

      const page = atEnd ? this.pages - 1 : Math.round(scrollLeft / this.pageWidth);
      this.page = Math.min(Math.max(page, 0), this.pages - 1);

      this.dotsContainer.querySelectorAll('.reviews-nav__dot').forEach((dot, index) => {
        const active = index === this.page;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      this.prevButton.disabled = scrollLeft <= 2;
      this.nextButton.disabled = atEnd;
    }

    goTo(page) {
      const target = Math.min(Math.max(page, 0), this.pages - 1);
      this.scrollToPosition(target * this.pageWidth);
    }

    scrollByPage(direction) {
      this.scrollToPosition(this.grid.scrollLeft + direction * this.pageWidth);
    }

    scrollToPosition(left) {
      const maxScroll = this.grid.scrollWidth - this.grid.clientWidth;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.grid.scrollTo({ left: Math.min(Math.max(left, 0), maxScroll), behavior: reduceMotion ? 'instant' : 'smooth' });
    }
  }

  customElements.define('reviews-slider', ReviewsSlider);
}
